import type { Exchange, Operation } from "@urql/vue";
import { useOnline } from "@vueuse/core";
import { filter, onPush, pipe } from "wonka";
import { useLogger } from "@/composables/logger";
import {
  getLocalStorageArray,
  upsertLocalStorageArray,
  removeFromLocalStorageArray
} from "@/utils/utils-dependent";

const { info, error } = useLogger("OFFLINE_RETRY_EXCHANGE");

const online = useOnline();
/*
  This exchange hooks into graphql requests, and when it finds a mutation operation while we're offline, it filters it out and saves
  it to local storage, this is to handle the case for when they come back online and need to redirect for authentication since the 
  tokens expired during the time they were offline.
*/

/*
  ************IMPORTANT************
  The initial retry request gets kicked off inside AuthProvider.vue by using a wathcer on shouldRetryCachedOperations.
  ************IMPORTANT************
*/

export const offlineRetryExchange: Exchange =
  ({ client, forward }) =>
  operations$ => {
    const operationResult$ = pipe(
      operations$,
      filter(op => {
        if (!online.value && op.kind === "mutation") {
          upsertLocalStorageArray("offline", op, "key");
          return false;
        }
        return true;
      }),
      forward
    );

    return pipe(
      operationResult$,
      onPush(op => {
        // this code handles completed graphql operations, we check if the operation
        // was one of the operations we saved into the cache, if it was the following
        // code will remove it from the array and return true.
        if (removeFromLocalStorageArray("offline", op.operation, "key")) {
          if (op.error) {
            error("Error in offline retry operation:", op.error);
          } else {
            info("Offline retry of operation succeeded", {
              operation: op.operation.key
            });
          }

          // after removing the finished operation, try to get the next cached operation from local storage
          const nextOfflineOperation: Operation =
            getLocalStorageArray("offline").shift();
          if (online.value && nextOfflineOperation) {
            // if there was a next offline operation and we're still online, re-execute it.
            info(
              "Executing next operation from cached operation array:",
              nextOfflineOperation.key
            );
            client.reexecuteOperation(nextOfflineOperation);
          }
        }
      })
    );
  };
